/*
 * چیزی که یک **مهمان** از یک سیگنال می‌بیند — یک شکلِ واحد برای هر دو
 * لینکِ بی‌حساب و هر دو اپ.
 *
 * ═══ چرا این فایل هست ═══
 *
 * مهمان از دو در وارد می‌شود و دو تابعِ متفاوتِ دیتابیس جوابش را می‌دهند:
 *
 *   /s/<token>   `get_signal_by_token`   یکی برای همه، جواب نمی‌گیرد.
 *   /i/<token>   `get_signal_by_invite`  مالِ یک نفرِ مشخص، جواب می‌گیرد.
 *
 * آن دو شکلِ خامِ snake_case کمی با هم فرق دارند، و تا امروز هر کدام از
 * چهار صفحه (دو لینک × دو اپ) خودش از رویشان متن می‌ساخت. نتیجه‌اش این
 * بود که همان سه خطِ «تاریخ را چطور بنویسیم» چهار بار کپی شده بود و
 * ترتیبش هم با صفحه‌ی داخلِ اپ یکی نبود.
 *
 * حالا هر چهار صفحه از همین یک تابع رد می‌شوند. یعنی «مهمان چه می‌بیند»
 * یک تصمیمِ تست‌شدنی است، نه چیزی که در چهار فایلِ UI پخش شده باشد.
 *
 * ═══ چه چیزی عمداً این‌جا نیست ═══
 *
 * فهرستِ جواب‌ها و «کی هست». همان قانونِ FOMO، این‌بار از سمتِ بیرون:
 * مهمان نمی‌بیند چه کسِ دیگری دعوت شده. دیتابیس هم هیچ‌وقت این را به این
 * دو تابع نمی‌دهد — پس این‌جا حذف نشده، اصلاً وجود ندارد.
 *
 * و گفتگو: پیام‌ها از این دریچه بیرون نمی‌آیند. مهمان سربرگش را می‌بیند
 * (تا بداند این سیگنال گفتگو دارد) ولی هیچ پیامی لو نمی‌رود — رابط
 * به‌جایش می‌گوید اپ را نصب کن.
 */
import { formatApprox } from '../lib/approxLabel.ts';
import { signalWhenLabel } from '../lib/dates.ts';
import { t } from '../lib/i18n.ts';
import type { ItemFieldIcon } from '../lib/itemFields.ts';
import type { ResponseKind, SignalEvent, SignalKind } from './types.ts';

/**
 * رخدادِ کاتالوگ، همان‌طور که `signal_event_json` سیمش می‌کند — snake_case
 * و خام (مایگریشن 0081). یک بار این‌جا نوشته شده و هر دو ریپوی مهمان از
 * همین‌جا برش می‌دارند، تا سه نسخه‌ی کمی متفاوت از یک شکل نداشته باشیم.
 */
export interface RawSignalEvent {
  id: string;
  venue?: string | null;
  address?: string | null;
  url?: string | null;
  image_url?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
}

/**
 * شکلِ خامی که از دیتابیس می‌آید.
 *
 * عمداً ساختاری نوشته شده و نه با importِ `InviteView`/`PublicSignalView`:
 * `domain` نباید به `data` وابسته شود، و هر دو آن نوع‌ها همین شکل را
 * برآورده می‌کنند. فیلدهای اختیاری همان‌هایی‌اند که فقط یکی از دو تابع
 * برمی‌گرداند.
 */
export interface GuestSignalSource {
  title: string;
  kind: string;
  note?: string | null;
  cost_note?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  date_only?: boolean | null;
  approx_label?: string | null;
  place_name?: string | null;
  status?: string | null;
  creator_name: string;
  /** فقط لینکِ شخصی: اسمی که سازنده برای این نفر نوشته. */
  invited_name?: string | null;
  /** فقط لینکِ شخصی: جوابِ فعلیِ همین نفر. */
  my_response?: ResponseKind | null;
  /** فقط لینکِ شخصی: رخداد شروع شده، جواب دیگر عوض نمی‌شود. */
  locked?: boolean | null;
  /** پر است فقط وقتی سیگنال از یک رخدادِ کاتالوگ زاده شده. */
  event?: RawSignalEvent | null;
}

/**
 * یک ردیفِ برچسب‌دار از «این دعوت به چیه»: کِی / کجا / چند.
 *
 * چرا برچسب‌دار و چرا این‌جا: صفحه‌ی مهمان این سه را سه پاراگرافِ بی‌برچسب
 * می‌نوشت — زمان کم‌رنگ، هزینه هم کم‌رنگ و چسبیده به یادداشت. کسی که با
 * پیامک می‌آید و اپ را نمی‌شناسد باید با یک نگاه بفهمد کدام عدد قیمت است و
 * کدام تاریخ. همان الگوی `itemFields.ts`ِ صفحه‌ی کشف، با همان آیکون‌ها و
 * همان برچسب‌ها، تا دو صفحه‌ی «جزئیاتِ یک چیز» یک شکل بخوانند.
 */
export interface GuestFact {
  name: 'when' | 'where' | 'price';
  /** هر دو اپ این سه آیکون را دارند (`ui/icons.tsx`). */
  icon: ItemFieldIcon;
  label: string;
  value: string;
}

/** جوابِ مهمان، وقتی اصلاً حق دارد جواب بدهد. */
export interface GuestAnswer {
  current: ResponseKind;
  /** دکمه‌ها دیده می‌شوند ولی کار نمی‌کنند — شروع شده یا لغو شده. */
  locked: boolean;
}

export interface GuestView {
  title: string;
  kind: SignalKind;
  creatorName: string;
  invitedName: string | null;
  note: string | null;
  costNote: string | null;
  placeName: string | null;
  /** متنِ آماده‌ی زمان، دقیقاً مثلِ صفحه‌ی داخلِ اپ. */
  when: string;
  cancelled: boolean;
  /**
   * رخدادی که سیگنال ازش زاده شده. وقتی هست، مکان و آدرس و صفحه‌ی رسمی
   * از این‌جا می‌آیند — نه از `placeName` که در آن حالت خالی‌ست.
   */
  event: SignalEvent | null;
  /**
   * کِی / کجا / چند — آماده‌ی رندر، بدونِ ردیفِ خالی. صفر تا سه عضو، و
   * «کِی» همیشه هست چون بی‌زمان هم «هماهنگ می‌کنیم» می‌شود.
   */
  facts: GuestFact[];
  /** `null` یعنی این لینک اصلاً جواب نمی‌گیرد (لینکِ عمومی). */
  answer: GuestAnswer | null;
}

const KINDS: SignalKind[] = ['definite', 'if_company', 'hosting', 'open_idea', 'poll', 'fyi'];

/** نوعِ ناشناخته نباید صفحه را بشکند — همان پیش‌فرضی که `kindMeta` هم دارد. */
const asKind = (k: string): SignalKind =>
  (KINDS as string[]).includes(k) ? (k as SignalKind) : 'definite';

/** خالیِ رشته‌ای هم یعنی «نداریم»، وگرنه صفحه یک خطِ سفیدِ بی‌دلیل نشان می‌دهد. */
const orNull = (v?: string | null): string | null => (v && v.trim() ? v : null);

export function guestView(s: GuestSignalSource): GuestView {
  const cancelled = s.status === 'cancelled';

  /*
   * ترتیبِ approx قبل از starts_at عمدی است و از `SignalPage` کپی شده:
   * سیگنالی که هم «آخرِ هفته» دارد و هم یک تاریخِ حدسی، توی اپ برچسبِ
   * حدسی را نشان می‌دهد. صفحه‌ی مهمان تا امروز برعکسش را می‌کرد، یعنی
   * دو نفر که به یک سیگنال نگاه می‌کردند دو زمانِ متفاوت می‌خواندند.
   */
  const when = s.approx_label
    ? formatApprox(s.approx_label)
    : s.starts_at
      ? signalWhenLabel({ startsAt: s.starts_at, endsAt: s.ends_at, dateOnly: s.date_only })
      : t('coordinate');

  /*
   * «کجا» دو منبع دارد و هیچ‌وقت هر دو: سیگنالِ دست‌ساز `place_name` دارد و
   * سیگنالی که از یک رخدادِ کاتالوگ زاده شده، اسمِ محل و آدرس را از خودِ
   * رخداد می‌آورد. یک ردیف برای هر دو، وگرنه یکی از دو حالت جای خالی نشان
   * می‌دهد — همان چیزی که «Light at Night Kayaking» را بی‌آدرس کرده بود.
   */
  const where = orNull(s.place_name)
    ?? [orNull(s.event?.venue), orNull(s.event?.address)].filter(Boolean).join(' · ');

  const facts: GuestFact[] = ([
    { name: 'when' as const, icon: 'clock' as const, label: t('fieldWhen'), value: when },
    { name: 'where' as const, icon: 'pin' as const, label: t('fieldWhere'), value: where },
    { name: 'price' as const, icon: 'tag' as const, label: t('fieldPrice'), value: orNull(s.cost_note) ?? '' },
  ]).filter((f) => f.value !== '');

  return {
    title: s.title,
    kind: asKind(s.kind),
    creatorName: s.creator_name,
    invitedName: orNull(s.invited_name),
    note: orNull(s.note),
    costNote: orNull(s.cost_note),
    placeName: orNull(s.place_name),
    when,
    cancelled,
    facts,
    // `id` تنها فیلدِ حتمیِ شیءِ رخداده؛ نبودش یعنی سیگنال به هیچ رخدادی
    // وصل نیست و رابط اصلاً کارتی نمی‌سازه.
    event: s.event?.id
      ? {
          id: s.event.id,
          venue: orNull(s.event.venue),
          address: orNull(s.event.address),
          url: orNull(s.event.url),
          imageUrl: orNull(s.event.image_url),
          startsAt: s.event.starts_at ?? null,
          endsAt: s.event.ends_at ?? null,
        }
      : null,
    // نبودِ `my_response` یعنی لینکِ عمومی — اون اصلاً ردیفِ گیرنده‌ای
    // نداره که جواب روش بشینه (مایگریشن 0067).
    answer: s.my_response
      ? { current: s.my_response, locked: !!s.locked || cancelled }
      : null,
  };
}
